import {
  Box,
  Button,
  ColumnLayout,
  Container,
  DatePicker,
  FormField,
  Grid,
  Header,
  KeyValuePairs,
  Link,
  Modal,
  PieChart,
  Popover,
  Select,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import { useMemo, useState } from 'react';

import { DashboardReport, SalesRepCurrencyReportRead, SalesRepReportRead } from '../common/types';
import { formatFinancial } from '../common/formatting';

import { CenteredHeader, ValueComparison } from '../components/reporting';


export type SalesRepMetricProps = {
  header: string;
  value: number | undefined;
  previousValue?: number;
  loading?: boolean;
  error?: Error | null;
  formatter?: (x: number) => string;
  description?: React.ReactNode;
  inverted?: boolean;
  variant?: 'percentage' | 'difference';
  insideContainer?: boolean;
  link?: string;
  info?: string;
  suffix?: string;
};

export function SalesRepMetric(props: SalesRepMetricProps) {
  const [open, setOpen] = useState(false);

  const formatter = props.formatter || ((x: number) => formatFinancial(x, 0));
  const content = (
    <Box textAlign="center" variant="div">
      {props.loading ? (
        <Spinner />
      ) : props.error ? (
        '-'
      ) : props.previousValue !== undefined && props.value !== undefined ? (
        <ValueComparison
          formatter={formatter}
          inverted={props.inverted}
          previousValue={props.previousValue}
          value={props.value}
          variant={props.variant}
          suffix={props.suffix}
        />
      ) : props.value ? (
        formatter(props.value)
      ) : (
        '-'
      )}
    </Box>
  );

  return (
    <SpaceBetween direction="vertical" size="xxxs">
      <CenteredHeader description={props.description}>{props.header}</CenteredHeader>
      {content}
    </SpaceBetween>
  );
}

export type SalesRepMetricsProps = {
  days: number;
  report?: SalesRepReportRead;
  loading: boolean;
  error?: Error | null;
};

export function SalesRepMetrics(props: SalesRepMetricsProps) {
  const { days, report, loading, error } = props;

  const currencyReportLookup = useMemo(() => {
    const lookup: Record<string, SalesRepCurrencyReportRead> = {};

    for (const currencyReport of report?.report.currency_reports ?? []) {
      lookup[currencyReport.currency] = currencyReport;
    }

    return lookup;
  }, [report]);

  const priorCurrencyReportLookup = useMemo(() => {
    const lookup: Record<string, SalesRepCurrencyReportRead> = {};

    for (const currencyReport of report?.prior_report.currency_reports ?? []) {
      lookup[currencyReport.currency] = currencyReport;
    }

    return lookup;
  }, [report]);

  return (
    <ColumnLayout
      columns={2}
      borders="vertical"
    >
    <SpaceBetween direction="vertical" size="m">
      <Header variant="h1">USA</Header>
      <Grid
        gridDefinition={[
          { colspan: { default: 12, xxs: 6 } },
          { colspan: { default: 12, xxs: 6 } },
          { colspan: { default: 12, xxs: 6 } },
          { colspan: { default: 12, xxs: 6 } },
        ]}
      >
        <SalesRepMetric
          description="Cumulative Fiscal Week-to-Date Revenue"
          error={error}
          header="Revenue WTD"
          loading={loading}
          value={currencyReportLookup["USD"]?.revenue_wtd ?? 0}
          previousValue={priorCurrencyReportLookup["USD"]?.revenue_wtd ?? 0}
          suffix="USD"
        />
        <SalesRepMetric
          description="Cumulative Fiscal Month-to-Date Revenue"
          error={error}
          header="Revenue MTD"
          loading={loading}
          value={currencyReportLookup["USD"]?.revenue_mtd ?? 0}
          previousValue={priorCurrencyReportLookup["USD"]?.revenue_mtd ?? 0}
          suffix="USD"
        />
        <SalesRepMetric
          description="Cumulative Fiscal Year-to-Date Revenue"
          error={error}
          header="Revenue YTD"
          loading={loading}
          value={currencyReportLookup["USD"]?.revenue_ytd ?? 0}
          previousValue={priorCurrencyReportLookup["USD"]?.revenue_ytd ?? 0}
          suffix="USD"
        />
        <SalesRepMetric
          description="Number of machines offline in USA"
          error={error}
          header="# Offline Machines"
          loading={loading}
          value={currencyReportLookup["USD"]?.offline_machines ?? 0}
          previousValue={priorCurrencyReportLookup["USD"]?.offline_machines ?? 0}
          suffix="devices"
          formatter={(x: number) => x.toLocaleString()}
          inverted
        />

      </Grid>
    </SpaceBetween>
    <SpaceBetween direction="vertical" size="m">
      <Header variant="h1">CAN</Header>
      <Grid
        gridDefinition={[
          { colspan: { default: 12, xxs: 6 } },
          { colspan: { default: 12, xxs: 6 } },
          { colspan: { default: 12, xxs: 6 } },
          { colspan: { default: 12, xxs: 6 } },
        ]}
      >
        <SalesRepMetric
          description="Cumulative Fiscal Week-to-Date Revenue"
          error={error}
          header="Revenue WTD"
          loading={loading}
          value={currencyReportLookup["CAD"]?.revenue_wtd ?? 0}
          previousValue={priorCurrencyReportLookup["CAD"]?.revenue_wtd ?? 0}
          suffix="CAD"
        />
        <SalesRepMetric
          description="Cumulative Fiscal Month-to-Date Revenue"
          error={error}
          header="Revenue MTD"
          loading={loading}
          value={currencyReportLookup["CAD"]?.revenue_mtd ?? 0}
          previousValue={priorCurrencyReportLookup["CAD"]?.revenue_mtd ?? 0}
          suffix="CAD"
        />
        <SalesRepMetric
          description="Cumulative Fiscal Year-to-Date Revenue"
          error={error}
          header="Revenue YTD"
          loading={loading}
          value={currencyReportLookup["CAD"]?.revenue_ytd ?? 0}
          previousValue={priorCurrencyReportLookup["CAD"]?.revenue_ytd ?? 0}
          suffix="CAD"
        />
        <SalesRepMetric
          description="Number of machines offline in CAN"
          error={error}
          header="# Offline Machines"
          loading={loading}
          value={currencyReportLookup["CAD"]?.offline_machines ?? 0}
          previousValue={priorCurrencyReportLookup["CAD"]?.offline_machines ?? 0}
          suffix="devices"
          formatter={(x: number) => x.toLocaleString()}
          inverted
        />

      </Grid>
    </SpaceBetween>
    </ColumnLayout>
  );
}
